import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "dslField",
    "filtersList",
    "customerCountValue",
    "countValue",
    "sendableCountValue",
    "spinner",
    "honestyBanner",
    "sendBlockedBanner",
    "errorBanner",
    "previewBody",
    "emptyHint",
    "addButton",
    "filterCapHint",
    "confirmUniverse",
    "confirmUniverseCheckbox",
    "confirmUniverseError",
    "matchTab",
    "sendableTab",
  ];

  static values = {
    previewUrl: String,
    bootstrap: String,
  };

  connect() {
    this.bootstrap = JSON.parse(this.bootstrapValue || "{}");
    const bootstrapDsl = this.bootstrap.dsl || {};
    this.filters = Array.isArray(bootstrapDsl.filters)
      ? JSON.parse(JSON.stringify(this.bootstrap.dsl.filters))
      : [];
    this.maxFilters = this.bootstrap.maxFilters || 10;
    this.debounceTimer = null;
    this.previewMode = "match";
    this.lastMatchRows = [];
    this.lastSendableRows = [];
    this.renderFilters();
    this.syncDslField();
    this.schedulePreview();
  }

  // The hidden field always carries every rendered row, so an incomplete row can
  // never be silently dropped into a wider audience than the operator sees.
  onSubmit(event) {
    if (this.incompleteIndexes().length) {
      event.preventDefault();
      this.renderFilters();
      this.scrollToFilters();
      return;
    }

    if (!this.filters.length && !this.fullUniverseConfirmed()) {
      event.preventDefault();
      this.syncUniverseConfirm();
      if (this.hasConfirmUniverseErrorTarget) {
        this.confirmUniverseErrorTarget.classList.remove("d-none");
      }
      if (this.hasConfirmUniverseTarget) {
        this.confirmUniverseTarget.scrollIntoView({ block: "center" });
      }
    }
  }

  onConfirmUniverseChange() {
    if (this.hasConfirmUniverseErrorTarget && this.fullUniverseConfirmed()) {
      this.confirmUniverseErrorTarget.classList.add("d-none");
    }
  }

  fullUniverseConfirmed() {
    return this.hasConfirmUniverseCheckboxTarget
      ? this.confirmUniverseCheckboxTarget.checked
      : false;
  }

  syncUniverseConfirm() {
    if (!this.hasConfirmUniverseTarget) return;
    this.confirmUniverseTarget.classList.toggle("d-none", this.filters.length > 0);
  }

  scrollToFilters() {
    if (!this.hasFiltersListTarget) return;
    this.filtersListTarget.scrollIntoView({ block: "center" });
  }

  showMatchPreview(event) {
    event.preventDefault();
    this.previewMode = "match";
    this.syncPreviewTabs();
    this.renderPreviewRows(this.lastMatchRows);
  }

  showSendablePreview(event) {
    event.preventDefault();
    this.previewMode = "sendable";
    this.syncPreviewTabs();
    this.renderPreviewRows(this.lastSendableRows);
  }

  syncPreviewTabs() {
    if (this.hasMatchTabTarget) {
      this.matchTabTarget.classList.toggle("active", this.previewMode === "match");
    }
    if (this.hasSendableTabTarget) {
      this.sendableTabTarget.classList.toggle("active", this.previewMode === "sendable");
    }
  }
  addFilter(event) {
    event.preventDefault();
    if (this.filters.length >= this.maxFilters) return;

    const type = event.currentTarget.dataset.type;
    this.filters.push(this.defaultFilter(type));
    this.renderFilters();
    this.syncDslField();
    this.schedulePreview();
  }

  removeFilter(event) {
    event.preventDefault();
    const index = Number(event.currentTarget.dataset.index);
    this.filters.splice(index, 1);
    this.renderFilters();
    this.syncDslField();
    this.schedulePreview();
  }

  onFilterChange(event) {
    const index = Number(event.currentTarget.dataset.index);
    const field = event.currentTarget.dataset.field;
    const filter = this.filters[index];
    if (!filter) return;

    if (field === "event_type") {
      filter.event_type = event.currentTarget.value;
      delete filter.subject_ids;
      delete filter.subjects;
      this.renderFilters();
    } else if (field === "subject_ids") {
      const selected = Array.from(event.currentTarget.selectedOptions).map((o) =>
        Number(o.value)
      );
      filter.subject_ids = selected;
    } else if (field === "purchase_subjects") {
      const selected = Array.from(event.currentTarget.selectedOptions).map((o) => {
        const [subject_type, subject_id] = o.value.split(":");
        return { subject_type, subject_id: Number(subject_id) };
      });
      filter.subjects = selected;
    } else if (field === "question_id") {
      filter.question_id = Number(event.currentTarget.value) || null;
      filter.option_ids = [];
      this.renderFilters();
    } else if (field === "option_ids") {
      filter.option_ids = Array.from(event.currentTarget.selectedOptions).map(
        (o) => o.value
      );
    } else if (field === "value") {
      filter.value = event.currentTarget.value;
    } else if (field === "webinar_id") {
      filter.webinar_id = Number(event.currentTarget.value) || null;
    }

    this.syncDslField();
    this.schedulePreview();
  }

  defaultFilter(type) {
    switch (type) {
      case "event_happened":
        return { type: "event_happened", event_type: "registered_webinar", subject_ids: [] };
      case "has_answered":
        return { type: "has_answered", question_id: null, option_ids: [] };
      case "member_status":
        return { type: "member_status", value: "active" };
      case "registered_but_did_not_join":
        return { type: "registered_but_did_not_join", webinar_id: null };
      default:
        return { type };
    }
  }

  completeFilters() {
    return this.filters
      .map((filter) => this.normalizeFilter(filter))
      .filter(Boolean);
  }

  // Every rendered row, complete or not. Incomplete rows serialise to their
  // partial shape so the server rejects them instead of quietly widening the DSL.
  dslFilters() {
    return this.filters.map(
      (filter) => this.normalizeFilter(filter) || this.partialFilter(filter)
    );
  }

  incompleteIndexes() {
    return this.filters.reduce((indexes, filter, index) => {
      if (!this.normalizeFilter(filter)) indexes.push(index);
      return indexes;
    }, []);
  }

  partialFilter(filter) {
    const type = filter && filter.type ? filter.type : "";

    switch (type) {
      case "event_happened":
        return { type: type, event_type: filter.event_type || "" };
      case "has_answered":
        return {
          type: type,
          question_id: filter.question_id || null,
          option_ids: Array.isArray(filter.option_ids)
            ? filter.option_ids.map(String)
            : [],
        };
      case "member_status":
        return { type: type, value: filter.value || "" };
      case "registered_but_did_not_join":
        return { type: type, webinar_id: filter.webinar_id || null };
      default:
        return { type: type };
    }
  }

  // AudienceDsl#normalize_int_ids accepts "12" as well as 12, so a DSL authored
  // outside the UI must keep its subject scoping through an edit.
  normalizeSubjectIds(value) {
    if (!Array.isArray(value)) return [];

    return value
      .map((id) => (typeof id === "string" ? id.trim() : id))
      .filter((id) => id !== "" && id !== null && id !== undefined)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  normalizeFilter(filter) {
    if (!filter || !filter.type) return null;

    if (filter.type === "event_happened") {
      if (!filter.event_type) return null;
      if (filter.event_type === "purchase_completed") {
        const subjects = Array.isArray(filter.subjects) ? filter.subjects : [];
        return {
          type: "event_happened",
          event_type: "purchase_completed",
          ...(subjects.length ? { subjects } : {}),
        };
      }
      const subject_ids = this.normalizeSubjectIds(filter.subject_ids);
      return {
        type: "event_happened",
        event_type: filter.event_type,
        ...(subject_ids.length ? { subject_ids } : {}),
      };
    }

    if (filter.type === "has_answered") {
      if (!filter.question_id || !Array.isArray(filter.option_ids) || !filter.option_ids.length) {
        return null;
      }
      return {
        type: "has_answered",
        question_id: Number(filter.question_id),
        option_ids: filter.option_ids.map(String),
      };
    }

    if (filter.type === "member_status") {
      if (!filter.value) return null;
      return { type: "member_status", value: filter.value };
    }

    if (filter.type === "registered_but_did_not_join") {
      if (!filter.webinar_id) return null;
      return {
        type: "registered_but_did_not_join",
        webinar_id: Number(filter.webinar_id),
      };
    }

    return null;
  }

  syncDslField() {
    const dsl = {
      version: 1,
      filters: this.dslFilters(),
    };
    if (this.hasDslFieldTarget) {
      this.dslFieldTarget.value = JSON.stringify(dsl);
    }
    this.updateCapHint();
    this.syncUniverseConfirm();
  }

  updateCapHint() {
    if (!this.hasFilterCapHintTarget) return;
    if (this.filters.length >= this.maxFilters) {
      this.filterCapHintTarget.textContent = `Maximum ${this.maxFilters} filters.`;
      if (this.hasAddButtonTarget) this.addButtonTarget.disabled = true;
    } else {
      this.filterCapHintTarget.textContent = "";
      if (this.hasAddButtonTarget) this.addButtonTarget.disabled = false;
    }
  }

  schedulePreview() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.runPreview(), 400);
  }

  async runPreview() {
    // A count computed from the complete rows only would be wider than the
    // audience on screen, which is the same lie as dropping the row on save.
    if (this.incompleteIndexes().length) {
      this.setLoading(false);
      this.clearErrors();
      if (this.hasCustomerCountValueTarget) this.customerCountValueTarget.textContent = "—";
      this.countValueTarget.textContent = "—";
      if (this.hasSendableCountValueTarget) this.sendableCountValueTarget.textContent = "—";
      this.renderPreviewRows([]);
      this.toggleHonesty(false);
      if (this.hasEmptyHintTarget) {
        this.emptyHintTarget.textContent =
          "Complete or remove the highlighted filters to see a count.";
      }
      return;
    }

    const dsl = {
      version: 1,
      filters: this.dslFilters(),
    };

    this.setLoading(true);
    this.clearErrors();

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ dsl }),
      });

      const data = await response.json();
      if (!response.ok) {
        this.showErrors(data.errors || []);
        if (this.hasCustomerCountValueTarget) this.customerCountValueTarget.textContent = "—";
        this.countValueTarget.textContent = "—";
        if (this.hasSendableCountValueTarget) this.sendableCountValueTarget.textContent = "—";
        this.lastMatchRows = [];
        this.lastSendableRows = [];
        this.renderPreviewRows([]);
        this.toggleHonesty(false);
        this.toggleSendBlocked(false);
        return;
      }

      if (this.hasCustomerCountValueTarget) {
        this.customerCountValueTarget.textContent = Number(
          data.customer_count || 0
        ).toLocaleString();
      }
      this.countValueTarget.textContent = Number(data.count || 0).toLocaleString();
      if (this.hasSendableCountValueTarget) {
        this.sendableCountValueTarget.textContent = Number(
          data.sendable_count || 0
        ).toLocaleString();
      }
      this.toggleHonesty(!!data.honesty);
      this.toggleSendBlocked(!!data.webinar_send_blocked);
      this.lastMatchRows = data.preview || [];
      this.lastSendableRows = data.sendable_preview || [];
      this.renderPreviewRows(
        this.previewMode === "sendable" ? this.lastSendableRows : this.lastMatchRows
      );
      if (this.hasEmptyHintTarget) {
        this.emptyHintTarget.textContent =
          (data.preview || []).length || data.count
            ? ""
            : dsl.filters.length
            ? "No matching people"
            : "Add filters to narrow";
      }
    } catch (_error) {
      this.showErrors([{ code: "malformed", path: "", message: "Could not refresh count" }]);
      if (this.hasCustomerCountValueTarget) this.customerCountValueTarget.textContent = "—";
      this.countValueTarget.textContent = "—";
      if (this.hasSendableCountValueTarget) this.sendableCountValueTarget.textContent = "—";
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("d-none", !loading);
    }
  }

  toggleHonesty(show) {
    if (!this.hasHonestyBannerTarget) return;
    this.honestyBannerTarget.classList.toggle("d-none", !show);
  }

  toggleSendBlocked(show) {
    if (!this.hasSendBlockedBannerTarget) return;
    this.sendBlockedBannerTarget.classList.toggle("d-none", !show);
  }

  clearErrors() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.classList.add("d-none");
    this.errorBannerTarget.textContent = "";
  }

  showErrors(errors) {
    if (!this.hasErrorBannerTarget) return;
    const messages = (errors || []).map((err) => {
      const ref = [err.reference_type, err.reference_id].filter(Boolean).join(" ");
      return [
        "Invalid filter",
        err.code ? `(${err.code})` : null,
        err.path ? `at ${err.path}` : null,
        ref ? `— ${ref}` : null,
        "— fix or remove",
      ]
        .filter(Boolean)
        .join(" ");
    });
    this.errorBannerTarget.textContent =
      messages.join(" ") || "Invalid filter — fix or remove";
    this.errorBannerTarget.classList.remove("d-none");
  }

  renderPreviewRows(rows) {
    if (!this.hasPreviewBodyTarget) return;
    if (!rows.length) {
      let hint = "Add a filter to narrow";
      if (this.completeFilters().length) {
        hint =
          this.previewMode === "sendable"
            ? "No sendable people"
            : "No matching people";
      }
      this.previewBodyTarget.innerHTML = `<tr><td colspan="4" class="text-muted ps-3 py-3">${hint}</td></tr>`;
      return;
    }

    this.previewBodyTarget.innerHTML = rows
      .map((row) => {
        const name = row.display_name || "—";
        const reason = row.match_reason || "—";
        const email = row.email || "";
        const path = row.journey_path || "#";
        return `<tr>
          <td class="ps-3">${this.escape(email)}</td>
          <td>${this.escape(name)}</td>
          <td>${this.escape(reason)}</td>
          <td class="pe-3"><a href="${this.escape(path)}" class="text-decoration-underline">Journey</a></td>
        </tr>`;
      })
      .join("");
  }

  renderFilters() {
    if (!this.hasFiltersListTarget) return;
    if (!this.filters.length) {
      this.filtersListTarget.innerHTML =
        '<p class="font-14 text-muted mb-2">No filters yet — count shows the full people universe.</p>';
      this.updateCapHint();
      return;
    }

    this.filtersListTarget.innerHTML = this.filters
      .map((filter, index) => this.filterRowHtml(filter, index))
      .join('<div class="font-12 text-muted my-2">AND</div>');
    this.updateCapHint();
  }

  filterRowHtml(filter, index) {
    const incomplete = !this.normalizeFilter(filter);
    const border = incomplete ? "border border-danger" : "border";
    const error = incomplete
      ? '<div class="text-danger font-12 mt-2" role="alert">Incomplete filter — complete or remove it.</div>'
      : "";

    return `<div class="${border} rounded-3 p-3 mb-2">
      ${this.filterBodyHtml(filter, index)}
      ${error}
    </div>`;
  }

  filterBodyHtml(filter, index) {
    const removeBtn = `<button type="button" class="btn btn-sm btn-outline-secondary" data-action="audience-composer#removeFilter" data-index="${index}" aria-label="Remove filter">×</button>`;

    if (filter.type === "event_happened") {
      return `<div class="d-flex justify-content-between align-items-start mb-2">
          <div class="fw-bold font-14">Event happened</div>
          ${removeBtn}
        </div>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="form-label fw-bold font-12">Event type</label>
            <select class="form-select" data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="event_type">
              ${this.optionsHtml(this.bootstrap.eventTypes || [], filter.event_type)}
            </select>
          </div>
          <div class="col-md-8">
            ${this.subjectControlHtml(filter, index)}
          </div>
        </div>`;
    }

    if (filter.type === "has_answered") {
      const question = (this.bootstrap.questions || []).find(
        (q) => Number(q.id) === Number(filter.question_id)
      );
      const optionChoices = question ? question.options : [];
      return `<div class="d-flex justify-content-between align-items-start mb-2">
          <div class="fw-bold font-14">Has answered…</div>
          ${removeBtn}
        </div>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label fw-bold font-12">Question</label>
            <select class="form-select" data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="question_id">
              <option value="">Select question</option>
              ${this.optionsHtml(this.bootstrap.questions || [], filter.question_id)}
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label fw-bold font-12">Option(s)</label>
            <select class="form-select" multiple data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="option_ids">
              ${this.optionsHtml(optionChoices, filter.option_ids || [])}
            </select>
            <div class="font-12 text-muted mt-1">OR within selected options</div>
          </div>
        </div>`;
    }

    if (filter.type === "member_status") {
      const radios = (this.bootstrap.memberStatuses || [])
        .map((status) => {
          const checked = filter.value === status.value ? "checked" : "";
          return `<div class="form-check">
            <input class="form-check-input" type="radio" name="member_status_${index}" id="member_status_${index}_${status.value}" value="${status.value}" ${checked}
              data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="value">
            <label class="form-check-label font-14" for="member_status_${index}_${status.value}">${this.escape(status.label)}</label>
          </div>`;
        })
        .join("");
      return `<div class="d-flex justify-content-between align-items-start mb-2">
          <div class="fw-bold font-14">Member status</div>
          ${removeBtn}
        </div>
        ${radios}`;
    }

    if (filter.type === "registered_but_did_not_join") {
      return `<div class="d-flex justify-content-between align-items-start mb-2">
          <div class="fw-bold font-14">Registered but did not join</div>
          ${removeBtn}
        </div>
        <label class="form-label fw-bold font-12">Webinar</label>
        <select class="form-select" data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="webinar_id">
          <option value="">Select webinar</option>
          ${this.optionsHtml(this.bootstrap.webinars || [], filter.webinar_id)}
        </select>
        <div class="font-12 text-muted mt-1">Registered for this webinar and did not join the same webinar.</div>`;
    }

    return `<div class="d-flex justify-content-between">
      <span class="text-muted">Unknown filter</span>${removeBtn}
    </div>`;
  }

  subjectControlHtml(filter, index) {
    const eventType = filter.event_type;
    if (eventType === "purchase_completed") {
      const choices = [
        ...(this.bootstrap.courses || []).map((c) => ({
          value: `Course:${c.id}`,
          label: `Course: ${c.name}`,
        })),
        ...(this.bootstrap.accountPlans || []).map((p) => ({
          value: `AccountPlan:${p.id}`,
          label: `Plan: ${p.name}`,
        })),
      ];
      const selected = (filter.subjects || []).map(
        (s) => `${s.subject_type}:${s.subject_id}`
      );
      return `
        <label class="form-label fw-bold font-12">Subject (optional — Any if empty)</label>
        <select class="form-select" multiple data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="purchase_subjects">
          ${choices
            .map((c) => {
              const sel = selected.includes(c.value) ? "selected" : "";
              return `<option value="${this.escape(c.value)}" ${sel}>${this.escape(c.label)}</option>`;
            })
            .join("")}
        </select>`;
    }

    let choices = [];
    if (["registered_webinar", "joined_webinar"].includes(eventType)) {
      choices = this.bootstrap.webinars || [];
    } else if (["registered_vsl", "viewed_vsl"].includes(eventType)) {
      choices = this.bootstrap.vsls || [];
    } else if (["course_enrolled", "course_completed"].includes(eventType)) {
      choices = this.bootstrap.courses || [];
    }

    return `
      <label class="form-label fw-bold font-12">Subject (optional — Any if empty)</label>
      <select class="form-select" multiple data-action="change->audience-composer#onFilterChange" data-index="${index}" data-field="subject_ids">
        ${this.optionsHtml(choices, filter.subject_ids || [])}
      </select>`;
  }

  optionsHtml(items, selected) {
    const selectedSet = new Set(
      Array.isArray(selected)
        ? selected.map(String)
        : [String(selected == null ? "" : selected)]
    );
    return (items || [])
      .map((item) => {
        const value = item.value != null ? item.value : item.id;
        const label = item.label != null ? item.label : item.name;
        const sel = selectedSet.has(String(value)) ? "selected" : "";
        return `<option value="${this.escape(String(value))}" ${sel}>${this.escape(
          String(label)
        )}</option>`;
      })
      .join("");
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }

  escape(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
